/**
 * The project room's fixed front side (PLAN.md WP0.5, förmåga 4): status and status line,
 * manually maintained promises/commitments/assumptions, and checklists instantiated from
 * templates. Status and the status line autosave; nothing here has a save button.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ApiError,
  api,
  type AttachmentInfo,
  type ChecklistInfo,
  type ChecklistTemplateInfo,
  type ProjectDetail,
  type ProjectItemKind,
  type ProjectStatus,
} from "../../api";
import { Dialog } from "../../components/Dialog";
import { FavoriteStar } from "../../components/FavoriteStar";
import { Button, Card, Field, Num } from "../../components/primitives";
import { SkeletonText } from "../../components/Skeleton";
import { useToast } from "../../components/toast";
import { ProjectStatusBadge, STATUS_LABELS } from "./ProjektPage";

const AUTOSAVE_DELAY_MS = 900;

// UI wording per the owner's adjustment 2026-08-12; the database keeps the original enum
// values (promise/commitment/assumption), so no migration and no broken rows.
const KIND_META: { kind: ProjectItemKind; title: string; hint: string; addLabel: string }[] = [
  {
    kind: "promise",
    title: "Avtalat",
    hint: "Vad som är avtalat med kunden",
    addLabel: "Lägg till avtalad punkt",
  },
  {
    kind: "commitment",
    title: "Moment & aktiviteter",
    hint: "Uppdragets moment och aktiviteter",
    addLabel: "Lägg till moment",
  },
  {
    kind: "assumption",
    title: "Antaganden",
    hint: "Osäkra tills de är överenskomna och dokumenterade — flytta då punkten till Avtalat",
    addLabel: "Lägg till antagande",
  },
];

export function ProjektRumPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const showToast = useToast();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [pageText, setPageText] = useState<string | null>(null);
  const [gallery, setGallery] = useState<AttachmentInfo[]>([]);
  const [checklistPickerOpen, setChecklistPickerOpen] = useState(false);
  const [removeChecklistTarget, setRemoveChecklistTarget] = useState<ChecklistInfo | null>(null);
  const noteTimer = useRef<number | undefined>(undefined);

  // The read view at the bottom: the project's wiki page as plain text, so archived captures
  // and notes are readable without leaving the room.
  useEffect(() => {
    const pageId = project?.page_id;
    if (!pageId) {
      setPageText(null);
      return;
    }
    let cancelled = false;
    void api
      .page(pageId)
      .then((page) => !cancelled && setPageText(page.text_cache))
      .catch(() => !cancelled && setPageText(null));
    return () => {
      cancelled = true;
    };
  }, [project?.page_id]);

  useEffect(() => {
    if (!projectId) {
      return;
    }
    let cancelled = false;
    void api
      .project(projectId)
      .then((loaded) => !cancelled && setProject(loaded))
      .catch(() => {
        showToast({ message: "Projektet kunde inte öppnas.", tone: "fail" });
        navigate("/projekt", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, navigate, showToast]);

  const patch = useCallback(
    async (body: { name?: string; status?: ProjectStatus; status_note?: string }) => {
      if (!projectId) {
        return;
      }
      try {
        const updated = await api.patchProject(projectId, body);
        setProject((current) =>
          current ? { ...updated, items: current.items, checklists: current.checklists } : updated,
        );
      } catch (error) {
        showToast({
          message: error instanceof ApiError ? error.message : "Kunde inte spara.",
          tone: "fail",
        });
      }
    },
    [projectId, showToast],
  );

  const onNoteChange = useCallback(
    (value: string) => {
      setProject((current) => (current ? { ...current, status_note: value } : current));
      window.clearTimeout(noteTimer.current);
      noteTimer.current = window.setTimeout(() => {
        void patch({ status_note: value });
      }, AUTOSAVE_DELAY_MS);
    },
    [patch],
  );

  const reload = useCallback(async () => {
    if (projectId) {
      setProject(await api.project(projectId));
    }
  }, [projectId]);

  // The gallery: every photo/PDF the project shows, refreshed with the room.
  useEffect(() => {
    if (projectId) {
      void api.projectGallery(projectId).then(setGallery);
    }
  }, [projectId, pageText]);

  if (!project) {
    return <SkeletonText lines={6} />;
  }

  return (
    <div className="grid content-start gap-s6">
      <Card hero>
        <div className="flex flex-wrap items-center gap-s4">
          <h1 className="text-h2 font-bold text-ink">{project.name}</h1>
          <FavoriteStar key={project.id} kind="project" targetId={project.id} />
          <ProjectStatusBadge status={project.status} />
          <div className="ml-auto flex items-center gap-s4">
            {project.page_id && (
              <Button variant="ghost" onClick={() => navigate(`/sidor/${project.page_id}`)}>
                Öppna wikisidan
              </Button>
            )}
            <label className="grid gap-s1">
              <span className="sr-only">Status</span>
              <select
                className="nb-field"
                value={project.status}
                onChange={(event) => void patch({ status: event.target.value as ProjectStatus })}
              >
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="mt-s5">
          <Field
            label="Statusrad"
            value={project.status_note}
            onChange={(event) => onNoteChange(event.target.value)}
            help="En mening om läget just nu. Sparas automatiskt."
          />
        </div>
      </Card>

      <div className="grid gap-s6 lg:grid-cols-3">
        {KIND_META.map((meta) => (
          <ItemSection
            key={meta.kind}
            project={project}
            kind={meta.kind}
            title={meta.title}
            hint={meta.hint}
            addLabel={meta.addLabel}
            onChanged={() => void reload()}
          />
        ))}
      </div>

      <Card>
        <div className="flex items-center gap-s4">
          <h2 className="nb-overline border-0 pb-0">Checklistor</h2>
          <Button
            variant="primary"
            className="ml-auto"
            onClick={() => setChecklistPickerOpen(true)}
          >
            Ny checklista
          </Button>
        </div>

        {project.checklists.length === 0 ? (
          <p className="mt-s4 text-body text-muted">
            Inga checklistor ännu. Skapa en från en mall — mallarna hanteras under Projekt →
            Checklistmallar.
          </p>
        ) : (
          <div className="mt-s5 grid gap-s6 md:grid-cols-2">
            {project.checklists.map((checklist) => (
              <ChecklistCard
                key={checklist.id}
                checklist={checklist}
                onChanged={() => void reload()}
                onRemove={() => setRemoveChecklistTarget(checklist)}
              />
            ))}
          </div>
        )}
      </Card>

      {gallery.length > 0 && (
        <Card>
          <h2 className="nb-overline">Bilagor</h2>
          <ul className="mt-s5 flex flex-wrap gap-s4">
            {gallery.map((attachment) => (
              <li key={attachment.id}>
                <a href={attachment.url} target="_blank" rel="noreferrer" title={attachment.filename}>
                  {attachment.mime_type.startsWith("image/") ? (
                    <img
                      src={attachment.thumbnail_url ?? attachment.url}
                      alt={attachment.filename}
                      loading="lazy"
                      className="h-s8 w-s8 rounded-card border border-line object-cover"
                    />
                  ) : (
                    <span className="flex h-s8 w-s8 items-center justify-center rounded-card border border-line bg-surface-2 text-title">
                      📄
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {project.page_id && (
        <Card sunken>
          <div className="flex items-center gap-s4">
            <h2 className="nb-overline border-0 pb-0">Projektsidan</h2>
            <Button
              variant="ghost"
              className="ml-auto"
              onClick={() => navigate(`/sidor/${project.page_id}`)}
            >
              Öppna i editorn
            </Button>
          </div>
          {pageText ? (
            <p className="mt-s4 whitespace-pre-wrap text-body text-ink">{pageText}</p>
          ) : (
            <p className="mt-s4 text-body text-muted">
              Projektsidan är tom ännu. Allt som arkiveras hit från Strömmen och skrivs i editorn
              blir läsbart här.
            </p>
          )}
        </Card>
      )}

      <ChecklistPicker
        open={checklistPickerOpen}
        projectId={project.id}
        onClose={() => setChecklistPickerOpen(false)}
        onCreated={() => {
          setChecklistPickerOpen(false);
          void reload();
        }}
      />

      {/* Removing a checklist throws away its ticked state — that is not undoable, so a dialog. */}
      <Dialog
        open={removeChecklistTarget !== null}
        title="Ta bort checklistan?"
        onClose={() => setRemoveChecklistTarget(null)}
        footer={
          <>
            <Button onClick={() => setRemoveChecklistTarget(null)}>Avbryt</Button>
            <Button
              variant="danger"
              onClick={() => {
                const target = removeChecklistTarget;
                setRemoveChecklistTarget(null);
                if (target) {
                  void api.removeChecklist(target.id).then(() => void reload());
                }
              }}
            >
              Ta bort
            </Button>
          </>
        }
      >
        <p className="text-body">
          ”{removeChecklistTarget?.name}” och alla avbockningar försvinner. Mallen påverkas inte.
        </p>
      </Dialog>
    </div>
  );
}

// --- front-side item lists ---------------------------------------------------------------------

function ItemSection({
  project,
  kind,
  title,
  hint,
  addLabel,
  onChanged,
}: {
  project: ProjectDetail;
  kind: ProjectItemKind;
  title: string;
  hint: string;
  addLabel: string;
  onChanged: () => void;
}) {
  const showToast = useToast();
  const [draft, setDraft] = useState("");
  const items = project.items.filter((item) => item.kind === kind);

  const add = useCallback(async () => {
    if (!draft.trim()) {
      return;
    }
    await api.addProjectItem(project.id, { kind, body: draft.trim() });
    setDraft("");
    onChanged();
  }, [draft, project.id, kind, onChanged]);

  const remove = useCallback(
    async (itemId: string, body: string) => {
      await api.removeProjectItem(itemId);
      onChanged();
      showToast({
        message: "Posten togs bort.",
        undo: () => {
          void api.addProjectItem(project.id, { kind, body }).then(onChanged);
        },
      });
    },
    [project.id, kind, onChanged, showToast],
  );

  return (
    <Card>
      <h2 className="nb-overline border-0 pb-0">{title}</h2>
      <p className="mt-s1 text-small text-muted">{hint}</p>

      {items.length > 0 && (
        <ul className="mt-s4 grid gap-s3">
          {items.map((item) => (
            <li key={item.id} className="group flex items-start gap-s3">
              <span className="min-w-0 flex-1 text-body text-ink">{item.body}</span>
              <button
                type="button"
                aria-label="Ta bort posten"
                className="text-small text-muted opacity-0 transition-[opacity] duration-control ease-nb hover:text-ink focus:opacity-100 group-hover:opacity-100"
                onClick={() => void remove(item.id, item.body)}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="mt-s4"
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <Field
          label={addLabel}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          help="Enter lägger till"
        />
      </form>
    </Card>
  );
}

// --- checklists ----------------------------------------------------------------------------------

function ChecklistCard({
  checklist,
  onChanged,
  onRemove,
}: {
  checklist: ChecklistInfo;
  onChanged: () => void;
  onRemove: () => void;
}) {
  const done = checklist.items.filter((item) => item.done).length;

  return (
    <div className="rounded-card border border-line bg-surface-2 p-s5">
      <div className="flex items-center gap-s4">
        <h3 className="text-body font-medium text-ink">{checklist.name}</h3>
        <span className="text-small text-muted">
          <Num>
            {done}/{checklist.items.length}
          </Num>
        </span>
        <button
          type="button"
          aria-label={`Ta bort checklistan ${checklist.name}`}
          className="ml-auto text-small text-muted hover:text-ink"
          onClick={onRemove}
        >
          ✕
        </button>
      </div>

      {checklist.items.length === 0 ? (
        <p className="mt-s3 text-small text-muted">Listan är tom.</p>
      ) : (
        <ul className="mt-s4 grid gap-s3">
          {checklist.items.map((item) => (
            <li key={item.id}>
              <label className="flex items-start gap-s3 text-body text-ink">
                <input
                  type="checkbox"
                  checked={item.done}
                  onChange={(event) => {
                    void api.toggleChecklistItem(item.id, event.target.checked).then(onChanged);
                  }}
                  style={{ accentColor: "var(--drop)" }}
                  className="mt-s1"
                />
                <span className={item.done ? "text-muted line-through" : undefined}>
                  {item.text}
                </span>
                {item.done && item.done_by && (
                  <span className="ml-auto text-small text-muted">{item.done_by}</span>
                )}
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChecklistPicker({
  open,
  projectId,
  onClose,
  onCreated,
}: {
  open: boolean;
  projectId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const showToast = useToast();
  const [templates, setTemplates] = useState<ChecklistTemplateInfo[]>([]);
  const [customName, setCustomName] = useState("");

  useEffect(() => {
    if (open) {
      void api.checklistTemplates().then(setTemplates);
    }
  }, [open]);

  const createFrom = useCallback(
    async (body: { template_id?: string; name?: string }) => {
      try {
        await api.createChecklist(projectId, body);
        setCustomName("");
        onCreated();
      } catch (error) {
        showToast({
          message: error instanceof ApiError ? error.message : "Checklistan kunde inte skapas.",
          tone: "fail",
        });
      }
    },
    [projectId, onCreated, showToast],
  );

  return (
    <Dialog open={open} title="Ny checklista" subtitle="Från mall eller tom" onClose={onClose}>
      <div className="grid gap-s5">
        {templates.length > 0 ? (
          <ul className="grid gap-s2">
            {templates.map((template) => (
              <li key={template.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-s4 rounded-control px-s4 py-s3 text-left text-body text-ink transition-[background] duration-control ease-nb hover:bg-surface-2"
                  onClick={() => void createFrom({ template_id: template.id })}
                >
                  <span>{template.name}</span>
                  <span className="text-small text-muted">
                    <Num>{template.items.length}</Num> punkter
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-body text-muted">
            Inga mallar ännu — de skapas under Projekt → Checklistmallar. Du kan också börja med
            en tom lista.
          </p>
        )}

        <form
          className="grid gap-s4 border-t border-line pt-s5"
          onSubmit={(event) => {
            event.preventDefault();
            if (customName.trim()) {
              void createFrom({ name: customName.trim() });
            }
          }}
        >
          <Field
            label="Eller en tom lista med eget namn"
            value={customName}
            onChange={(event) => setCustomName(event.target.value)}
          />
          <Button
            variant="primary"
            type="submit"
            className="justify-self-end"
            disabled={!customName.trim()}
          >
            Skapa tom lista
          </Button>
        </form>
      </div>
    </Dialog>
  );
}
